'use client';

import { c as _c } from "react/compiler-runtime";
import { ReactNode, useLayoutEffect } from 'react';
import { usePathname } from 'next/navigation';
import { I18nextProvider } from 'react-i18next';
import i18n, { initI18n } from '@/lib/i18n/config';
import { applyAppLanguage, applyLanguageFromUserSettings } from '@/hooks/useAppLanguage';
import { applyLandingLanguage } from '@/hooks/useLandingLanguage';
import { useUserSettings } from '@/contexts/UserSettingsContext';
import { isAuthenticatedClient, resolveAppLanguage } from '@/lib/i18n/resolve-app-language';
import { getCachedAppLanguage, hasCachedAppLanguage } from '@/lib/i18n/language-storage';
import { getLandingLanguage } from '@/lib/i18n/landing-language-storage';
import { isPublicLoginRoute, isLandingRoute } from '@/lib/i18n/public-serbian-routes';
import { syncLandingDocumentMeta } from '@/lib/i18n/landing-document-meta';
function syncAppDocumentMeta(lang: string) {
  document.documentElement.lang = lang;
  document.title = i18n.t('common:appDocumentTitle');
  const meta = document.querySelector('meta[name="description"]');
  if (meta) {
    meta.setAttribute('content', i18n.t('common:appDocumentDescription'));
  }
}

/** Keeps i18n aligned with cache (immediate) and UserSettings API (authoritative). */
function LanguageSync(t0) {
  const $ = _c(13);
  const {
    children
  } = t0;
  const pathname = usePathname();
  const {
    settings,
    loading: settingsLoading
  } = useUserSettings();
  let t1;
  if ($[0] !== pathname || $[1] !== settings?.language || $[2] !== settingsLoading) {
    t1 = () => {
      if (isLandingRoute(pathname)) {
        applyLandingLanguage(getLandingLanguage());
        return;
      }
      if (isPublicLoginRoute(pathname)) {
        const lang = getLandingLanguage();
        applyAppLanguage(lang, {
          cache: false
        });
        syncAppDocumentMeta(lang);
        return;
      }
      const authenticated = isAuthenticatedClient();
      if (!authenticated) {
        const lang_0 = resolveAppLanguage({
          isAuthenticated: false,
          settingsLoaded: true
        });
        applyAppLanguage(lang_0);
        syncAppDocumentMeta(lang_0);
        return;
      }
      if (settingsLoading) {
        if (hasCachedAppLanguage()) {
          const lang_1 = getCachedAppLanguage();
          applyAppLanguage(lang_1);
          syncAppDocumentMeta(lang_1);
        }
        return;
      }
      const lang_2 = applyLanguageFromUserSettings(settings?.language);
      syncAppDocumentMeta(lang_2);
    };
    $[0] = pathname;
    $[1] = settings?.language;
    $[2] = settingsLoading;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = settings?.language;
  let t3;
  if ($[4] !== pathname || $[5] !== settingsLoading || $[6] !== t2) {
    t3 = [pathname, settingsLoading, t2];
    $[4] = pathname;
    $[5] = settingsLoading;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useLayoutEffect(t1, t3);
  let t4;
  let t5;
  if ($[8] !== pathname) {
    t4 = () => {
      const onLanguageChanged = () => {
        if (isLandingRoute(pathname)) {
          syncLandingDocumentMeta();
        } else {
          syncAppDocumentMeta(i18n.language);
        }
      };
      i18n.on("languageChanged", onLanguageChanged);
      return () => {
        i18n.off("languageChanged", onLanguageChanged);
      };
    };
    t5 = [pathname];
    $[8] = pathname;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useLayoutEffect(t4, t5);
  let t6;
  if ($[11] !== children) {
    t6 = <>{children}</>;
    $[11] = children;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
function bootstrapLanguage(pathname: string) {
  if (isLandingRoute(pathname)) {
    applyLandingLanguage(getLandingLanguage());
    return;
  }
  if (isPublicLoginRoute(pathname)) {
    applyAppLanguage(getLandingLanguage(), {
      cache: false
    });
    return;
  }
  applyAppLanguage(getCachedAppLanguage());
}
export default function I18nProvider(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  if (typeof window !== "undefined" && !i18n.isInitialized) {
    bootstrapLanguage(window.location.pathname);
  } else {
    if (typeof window === "undefined") {
      initI18n(getLandingLanguage());
    }
  }
  let t1;
  if ($[0] !== children) {
    t1 = <I18nextProvider i18n={i18n}><LanguageSync>{children}</LanguageSync></I18nextProvider>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}